import { useAtomValue } from 'jotai';
import { LockIcon } from 'lucide-react';
import { Fragment, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { cn, TableRow } from '@crossnokaye/ux-common';

import { Sequence, SequenceItem } from '@repo/sdk/models/components';

import { AddStageColumnCell } from '@/components/sequencer-table/add-stage-column';
import { EquipmentCell } from '@/components/sequencer-table/equipment-cell';
import {
  addStageToSequence,
  cycleStageInSequence,
  enableRowInSequence,
  getNestedValue,
  removeRowFromSequence,
  removeStageFromSequence,
} from '@/components/sequencer-table/helpers';
import { SequenceCell } from '@/components/sequencer-table/sequence-cell';
import { SequenceEditorTable } from '@/components/sequencer-table/sequence-editor-table';
import {
  cosmicActiveAtom,
  cosmicFallbackStageValueAtom,
  currentStageValueAtom,
  sequencesWithValuesAtom,
  SequenceWithValues,
  siblingSequenceValuesAtom,
} from '@/features/equipment-browser/equipment-details/state/sequencer-editor.state';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';

import { getCompressorHighlightState } from './compressor-highlight-state';
import { CompressorScheduleChart } from './compressor-schedule-chart/compressor-schedule-chart';
import { CompressorSequencerInstructionsContent } from './compressor-sequencer-instructions-content';
import {
  CompressorSequencerStage,
  cycleCompressorSequencerStageValue,
  deriveCompressorSequencerIcon,
  findLockingSibling,
  getCompressorSequencerCellStyles,
  getCompressorSequencerOverlayProperties,
  getCompressorSequenceState,
  trimBackground,
} from './helpers';
import { SequencerInstructions } from '../components/sequencer-instructions';
import { SequencerLegend } from '../components/sequencer-legend';
import { activeStageLegendItem, standardLegendItems } from '../components/sequencer-legend-items';
import { SequencerSectionTemplate } from '../components/sequencer-section-template';

/**
 * Returns the content of the cell for a compressor sequencer stage.
 *
 * @param stageValue - The stage value to get the cell content for.
 * @returns The content of the cell for a compressor sequencer stage.
 */
const getCompressorSequencerCellContent = (stageValue: number): React.ReactNode => {
  switch (stageValue) {
    case CompressorSequencerStage.UNAVAILABLE:
      return <LockIcon className={cn('glyph glyph-sm stroke-muted-foreground')} />;
  }
  return null;
};

interface CompressorSequencerEditorProps {
  sequenceWithValues: SequenceWithValues;
}

const getStageValue = (
  sequence: Sequence | undefined,
  rowIndex: number,
  columnIndex: number
): number => {
  return sequence?.table?.[rowIndex]?.stageValues?.[columnIndex] || 0;
};

const CompressorSequenceEditor = ({ sequenceWithValues }: CompressorSequencerEditorProps) => {
  const form = useFormContext();
  const activeStage = useAtomValue(currentStageValueAtom);
  const cosmicActive = useAtomValue(cosmicActiveAtom);
  const cosmicFallbackStage = useAtomValue(cosmicFallbackStageValueAtom);
  const siblingSequences = useAtomValue(siblingSequenceValuesAtom);
  const { isDisabled: disabledByPermissions, tooltipText: disabledTooltipText } =
    useActionPermission({
      privilege: PRIVILEGES.writeCommands,
    });
  // Get the form field key from the ref ID
  const formFieldKey = sequenceWithValues.ref?.id ? `sequencer.${sequenceWithValues.ref.id}` : '';

  // Get the current sequence value from the form
  const sequenceValue = (formFieldKey ? form.watch(formFieldKey) : undefined) as
    | Sequence
    | undefined;

  const handleRemoveRowClicked = (sequence: Sequence | undefined, rowIndex: number) => {
    if (!sequence || !formFieldKey) return;
    const updatedSequence = removeRowFromSequence(sequence, rowIndex);
    form.setValue(formFieldKey, updatedSequence, { shouldDirty: true });
  };

  const handleAddRowClicked = (sequence: Sequence | undefined, rowIndex: number) => {
    if (!sequence || !formFieldKey) return;
    const updatedSequence = enableRowInSequence(sequence, rowIndex, CompressorSequencerStage.OFF);
    form.setValue(formFieldKey, updatedSequence, { shouldDirty: true });
  };

  const handleAddStageClicked = (sequence: Sequence | undefined) => {
    if (!sequence || !formFieldKey) return;
    const updatedSequence = addStageToSequence(sequence, CompressorSequencerStage.OFF);
    form.setValue(formFieldKey, updatedSequence, { shouldDirty: true });
  };

  const handleRemoveStageClicked = (sequence: Sequence | undefined) => {
    if (!sequence || !formFieldKey) return;
    const updatedSequence = removeStageFromSequence(sequence);
    form.setValue(formFieldKey, updatedSequence, { shouldDirty: true });
  };

  const handleStageClicked = (
    sequence: Sequence | undefined,
    rowIndex: number,
    columnIndex: number
  ) => {
    if (!sequence || !formFieldKey) return;
    const updatedSequence = cycleStageInSequence(
      sequence,
      rowIndex,
      columnIndex,
      cycleCompressorSequencerStageValue
    );
    form.setValue(formFieldKey, updatedSequence, { shouldDirty: true });
  };

  const maxStages = Math.max(
    ...(sequenceValue?.table?.map((seq: any) => seq.stageValues.length - 1) || [])
  );
  const columnTitles = Array.from({ length: maxStages }, (_, i) => `Stage ${i + 1}`);
  const rowTitles = sequenceValue?.table?.map((item: SequenceItem) => item.name) || [];

  const overlayProperties = getCompressorSequencerOverlayProperties(sequenceValue);
  const defaultValues = form?.formState?.defaultValues;
  const showTableNotInControl = cosmicActive === true;
  const showChartNotInControl = cosmicActive === false;
  const { activeStageLegendLabel, highlightedStage } = getCompressorHighlightState({
    activeStage,
    cosmicActive,
    cosmicFallbackStage,
  });
  const compressorLegendItems = useMemo(() => {
    return [
      ...standardLegendItems,
      {
        icon: <div className="w-4 h-4 rounded-full" style={{ background: trimBackground }} />,
        label: 'Trim',
      },
      {
        ...activeStageLegendItem,
        label: activeStageLegendLabel,
      },
    ];
  }, [activeStageLegendLabel]);

  const originalSequence = defaultValues
    ? (getNestedValue(defaultValues, formFieldKey) as Sequence)
    : undefined;

  return (
    <Fragment key="compressor-sequencer-editor">
      <SequencerSectionTemplate
        legend={null}
        onAddStage={() => handleAddStageClicked(sequenceValue)}
        title={sequenceWithValues.label}
      >
        <div className="flex flex-col gap-4">
          {showTableNotInControl ? (
            <div className="rounded-md border border-ckds-base-caution/30 bg-ckds-base-caution/10 px-3 py-2 text-sm font-semibold text-ckds-base-caution">
              Cosmic sequencer is currently in control. This table is not the active controller
              right now.
            </div>
          ) : null}
          <div>
            <SequenceEditorTable
              activeStage={highlightedStage}
              onAddStageClicked={() => handleAddStageClicked(sequenceValue)}
              onRemoveStageClicked={() => handleRemoveStageClicked(sequenceValue)}
              overlayProperties={overlayProperties}
              sequence={sequenceValue}
            >
              {rowTitles.map((row: string, rowIndex: number): React.ReactNode => {
                const isRowDisabled =
                  sequenceValue?.table?.[rowIndex]?.stageValues?.every(
                    (stageValue: number) => stageValue === 0
                  ) ?? true;

                const lockedByLabel = findLockingSibling(row, siblingSequences);
                const isLockedByOther = !!lockedByLabel;

                return (
                  <TableRow key={`${row}_row_${rowIndex}`}>
                    <EquipmentCell
                      icon={deriveCompressorSequencerIcon()}
                      isLockedByOther={isLockedByOther}
                      isRowDisabled={isRowDisabled}
                      lockedByLabel={lockedByLabel}
                      onAddRow={() => handleAddRowClicked(sequenceValue, rowIndex)}
                      onRemoveRow={() => handleRemoveRowClicked(sequenceValue, rowIndex)}
                      rowTitle={rowTitles[rowIndex]}
                    />
                    {columnTitles.map((column: string, columnIndex: number) => {
                      // Adjusted column index because the table in the sequencer always
                      // has zero at the first index (all devices off).
                      const adjustedColumnIndex = columnIndex + 1;

                      // Get the stage value for the current cell.
                      const stageValue = getStageValue(
                        sequenceValue,
                        rowIndex,
                        adjustedColumnIndex
                      );

                      // Get the styles for a Cell in a Compressor Sequencer, based on the stage.
                      const { className, style } = getCompressorSequencerCellStyles(stageValue);

                      // Calculate if this cell is dirty by comparing with original value
                      const originalValue = getStageValue(
                        originalSequence,
                        rowIndex,
                        adjustedColumnIndex
                      );
                      const isDirty = originalValue !== stageValue;

                      // Highlight the column if it matches the active stage
                      const isActiveColumn = highlightedStage === adjustedColumnIndex;
                      const tooltipContent = getCompressorSequenceState(stageValue);
                      const isUnavailable = stageValue === 0;

                      const cellDisabledTooltipText = isLockedByOther
                        ? `Remove this equipment from one sequencer before editing stages`
                        : disabledTooltipText;

                      return (
                        <SequenceCell
                          className={className}
                          disabledTooltipText={cellDisabledTooltipText}
                          isColumnActive={isActiveColumn ?? false}
                          isDisabled={isLockedByOther || disabledByPermissions}
                          isDirty={isDirty}
                          isUnavailable={isUnavailable}
                          key={`${column}_cell_${row}_row_${rowIndex}_column_${adjustedColumnIndex}`}
                          onClick={() => {
                            handleStageClicked(sequenceValue, rowIndex, adjustedColumnIndex);
                          }}
                          original={
                            originalValue !== undefined
                              ? getCompressorSequenceState(originalValue)
                              : undefined
                          }
                          style={style}
                          tooltipContent={tooltipContent}
                        >
                          {getCompressorSequencerCellContent(stageValue)}
                        </SequenceCell>
                      );
                    })}
                    <AddStageColumnCell />
                  </TableRow>
                );
              })}
            </SequenceEditorTable>
          </div>
          <SequencerLegend items={compressorLegendItems} />
          <SequencerInstructions id="compressor">
            <CompressorSequencerInstructionsContent />
          </SequencerInstructions>
        </div>
      </SequencerSectionTemplate>

      <CompressorScheduleChart
        compressorOrder={rowTitles}
        showNotInControl={showChartNotInControl}
      />
    </Fragment>
  );
};

export const CompressorSequencerEditor = () => {
  const sequenceWithValues = useAtomValue(sequencesWithValuesAtom).at(0);
  if (!sequenceWithValues) {
    return null;
  }
  return <CompressorSequenceEditor sequenceWithValues={sequenceWithValues} />;
};
